import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCornerDownLeft, LuGauge, LuLayoutGrid, LuMapPin, LuPlug } from 'react-icons/lu';
import {
  MIN_QUERY_LENGTH,
  type SearchGroup,
  type SearchResult,
  type SearchResultGroup,
} from '@/modules/topbar/data/useGlobalSearch';
import styles from './SearchResults.module.css';

const GROUP_ICON: Record<SearchGroup, ReactNode> = {
  pages: <LuLayoutGrid aria-hidden="true" />,
  stations: <LuPlug aria-hidden="true" />,
  locations: <LuMapPin aria-hidden="true" />,
  meteringPoints: <LuGauge aria-hidden="true" />,
};

interface SearchResultsProps {
  listboxId: string;
  /** `${listboxId}-<result id>` of the highlighted option, if any. */
  activeOptionId: string | undefined;
  groups: SearchResultGroup[];
  query: string;
  isFetching: boolean;
  /** A data source failed — distinguished from "no matches", which it is not. */
  isError: boolean;
  onSelect: (result: SearchResult) => void;
  onHighlight: (result: SearchResult) => void;
}

export function optionDomId(listboxId: string, resultId: string): string {
  return `${listboxId}-${resultId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

/**
 * The palette's result list — shared by the inline (anchored panel) and the
 * dialog variant of the search field.
 *
 * Options are not focusable: focus stays in the input and the highlight is
 * published with `aria-activedescendant`, the listbox half of the combobox
 * pattern the field implements.
 */
export function SearchResults({
  listboxId,
  activeOptionId,
  groups,
  query,
  isFetching,
  isError,
  onSelect,
  onHighlight,
}: SearchResultsProps) {
  const { t } = useTranslation();
  const trimmed = query.trim();

  if (trimmed.length < MIN_QUERY_LENGTH) {
    return (
      <p className={styles.status}>{t('topbar.search.minChars', { min: MIN_QUERY_LENGTH })}</p>
    );
  }

  if (groups.length === 0) {
    // Error outranks "no matches": a failed source must not read as an empty fleet.
    return (
      <p className={styles.status} {...(isError && !isFetching ? { role: 'alert' } : {})}>
        {isFetching
          ? t('topbar.search.searching')
          : isError
            ? t('topbar.search.error')
            : t('topbar.search.empty', { query: trimmed })}
      </p>
    );
  }

  return (
    <>
      <div id={listboxId} role="listbox" aria-label={t('topbar.search.resultsLabel')}>
        {groups.map((entry) => (
          // biome-ignore lint/a11y/useSemanticElements: a listbox groups its options with role="group", not <fieldset>
          <div
            key={entry.group}
            role="group"
            aria-labelledby={`${listboxId}-${entry.group}-label`}
            className={styles.group}
          >
            <div className={styles.groupLabel} id={`${listboxId}-${entry.group}-label`}>
              {t(`topbar.search.groups.${entry.group}`)}
            </div>
            {entry.items.map((item) => {
              const id = optionDomId(listboxId, item.id);
              const isActive = id === activeOptionId;
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard (aria-activedescendant)
                <div
                  key={item.id}
                  id={id}
                  role="option"
                  aria-selected={isActive}
                  tabIndex={-1}
                  className={styles.option}
                  data-active={isActive || undefined}
                  // Keep focus in the input so the panel is not torn down before
                  // the click lands.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => onHighlight(item)}
                  onClick={() => onSelect(item)}
                >
                  <span className={styles.optionIcon}>{GROUP_ICON[item.group]}</span>
                  <span className={styles.optionText}>
                    <span className={styles.optionTitle}>{item.title}</span>
                    {item.subtitle && (
                      <span className={styles.optionSubtitle}>{item.subtitle}</span>
                    )}
                  </span>
                  {isActive && (
                    <LuCornerDownLeft className={styles.optionEnter} aria-hidden="true" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {isFetching ? (
        <p className={styles.footer} aria-hidden="true">
          {t('topbar.search.searching')}
        </p>
      ) : isError ? (
        // Partial results: page matches render, but a data source failed.
        <p className={styles.footer} role="alert">
          {t('topbar.search.error')}
        </p>
      ) : null}
    </>
  );
}
